/**
 * DTO — то, что реально уходит клиенту.
 *
 * Prisma-модели наружу не отдаются никогда: у них нет понятия «зритель», и
 * любое добавление колонки автоматически расширяет выдачу API. DTO — явный
 * контракт, который приходится менять осознанно.
 */

import type { AccessLevel, Role } from '@tw/permissions';
import type { LastMessageStatus } from './telegram-gateway.js';

export type UserStatus = 'ACTIVE' | 'SUSPENDED' | 'DEACTIVATED';

export type TelegramAccountStatus = 'PENDING_AUTH' | 'ACTIVE' | 'DISABLED' | 'ARCHIVED';

export type TelegramConnectionState =
  | 'CONNECTED'
  | 'RECONNECTING'
  | 'AUTHORIZATION_REQUIRED'
  | 'DISCONNECTED'
  | 'ERROR';

export type ChatInternalStatus = 'NEW' | 'IN_PROGRESS' | 'WAITING' | 'DONE' | 'ARCHIVED';

// ─────────────────────────────────────────────────────────────────────────────
// Пользователи
// ─────────────────────────────────────────────────────────────────────────────

/** Полная карточка пользователя. Отдаётся только ADMIN. */
export interface UserDto {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  status: UserStatus;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /**
   * Команда сотрудника.
   *
   * Может отсутствовать: часть выборок поле не запрашивает, а клиент и сервер
   * обновляются не одновременно. Читать защищённо.
   */
  folderId?: string | null;
}

/**
 * Урезанная карточка для Senior Manager.
 *
 * Отдельный тип, а не Partial<UserDto>: полный объект удовлетворяет Partial, и
 * компилятор не поймает случайную отдачу email'ов. Разделение типов — это и
 * есть механизм защиты.
 */
export interface UserSummaryDto {
  id: string;
  displayName: string;
  role: Role;
  status: UserStatus;
}

/** Собственный профиль — то, что возвращает /api/auth/me. */
export interface CurrentUserDto {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  status: UserStatus;
  mustChangePassword: boolean;
  /** Права, вычисленные бэкендом. Фронтенд использует их ТОЛЬКО для UI. */
  permissions: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Telegram-аккаунты
// ─────────────────────────────────────────────────────────────────────────────

/** Общая часть — видна всем, у кого есть telegram.account.view. */
export interface TelegramAccountBaseDto {
  id: string;
  alias: string;
  status: TelegramAccountStatus;
  connectionState: TelegramConnectionState;
  unreadCount: number;
  /**
   * Команда, за которой закреплён аккаунт (папка второго уровня), и её ветка.
   * Это оргструктура компании, а не Telegram-identity, поэтому видна и в
   * маскированном виде. null — аккаунт вне команды.
   */
  team?: { id: string; name: string } | null;
  branch?: { id: string; name: string } | null;
}

/**
 * Маскированный вид. Всё, что видит INTERN.
 *
 * Никаких Telegram-производных полей: ни displayName, ни username, ни phone,
 * ни telegramUserId, ни текста ошибки подключения (он часто содержит номер).
 * Отсутствуют физически, а не равны null.
 */
export interface MaskedTelegramAccountDto extends TelegramAccountBaseDto {
  masked: true;
}

/** Полный вид. Требует telegram.account.identity.view. */
export interface TelegramAccountDto extends TelegramAccountBaseDto {
  masked: false;
  telegramUserId: string | null;
  displayName: string | null;
  username: string | null;
  phone: string | null;
  /**
   * Есть ли на аккаунте Telegram Premium.
   *
   * Только в полном виде, не в маскированном. Стажёру знать свойства скрытого
   * от него аккаунта незачем: alias прячет, чей это номер, и подписка — такая
   * же характеристика владельца, как имя.
   *
   * null означает «не знаем»: рантайм аккаунта не поднят, и спросить Telegram
   * не у кого. Это не то же самое, что «подписки нет».
   */
  isPremium: boolean | null;
  lastConnectionError: string | null;
  connectedAt: string | null;
  connectedBy: UserSummaryDto | null;
  seniorManager: UserSummaryDto | null;
  assignedUsers: AccountAssignmentDto[];
  createdAt: string;
}

/**
 * Дискриминант `masked` существует для фронтенда: без него клиентский код
 * вынужден проверять наличие полей (`'phone' in account`), что легко написать
 * неверно. С дискриминантом сужение типа делает компилятор.
 */
export type AnyTelegramAccountDto = MaskedTelegramAccountDto | TelegramAccountDto;

export function isMaskedAccount(a: AnyTelegramAccountDto): a is MaskedTelegramAccountDto {
  return a.masked;
}

// ─────────────────────────────────────────────────────────────────────────────
// Назначения
// ─────────────────────────────────────────────────────────────────────────────

export interface AccountAssignmentDto {
  id: string;
  telegramAccountId: string;
  user: UserSummaryDto;
  accessLevel: AccessLevel;
  /** Эффективный уровень с учётом потолка роли. Может быть ниже accessLevel. */
  effectiveAccessLevel: AccessLevel;
  assignedBy: UserSummaryDto | null;
  createdAt: string;
}

export interface SeniorAssignmentDto {
  id: string;
  telegramAccountId: string;
  seniorManager: UserSummaryDto;
  assignedBy: UserSummaryDto | null;
  createdAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Чаты и сообщения
// ─────────────────────────────────────────────────────────────────────────────

export interface TelegramChatDto {
  id: string;
  /** Через какой аккаунт пришёл чат — обязательное поле общего inbox (§5 ТЗ). */
  account: AnyTelegramAccountDto;
  title: string | null;
  type: string;
  peerUsername: string | null;
  /**
   * Превью последнего сообщения. Может отсутствовать: у чата без переписки
   * его нет, и подставлять туда пустую строку нельзя — она читается как
   * «сообщений нет», хотя это не так.
   */
  /**
   * Заглушил ли ТЕКУЩИЙ зритель этот чат.
   *
   * Настройка поимённая и наша: в Telegram она не уходит. Аккаунт общий на
   * нескольких менеджеров, и телеграмная беззвучность заглушила бы чат всем
   * разом, включая тех, кто об этом не просил.
   */
  muted: boolean;
  lastMessagePreview: string | null;
  /** Для галочек в списке чатов; null — неизвестно (чат синхронизирован раньше). */
  lastMessageStatus: LastMessageStatus | null;
  lastMessageAt: string | null;
  unreadCount: number;
  /**
   * Есть ли у собеседника аватар.
   *
   * Речь именно о фотографии СОБЕСЕДНИКА — она не является identity нашего
   * аккаунта и маскирования не требует. Фотографию самого подключённого
   * аккаунта мы не показываем нигде: для стажёра это была бы прямая утечка
   * того, что скрывает alias (§7 ТЗ).
   */
  hasPhoto: boolean;
  /**
   * Закреплён в Telegram. Зеркало телеграмного закрепа на этом аккаунте, а
   * не внутренняя пометка: видно и в Telegram Desktop. Читать защищённо —
   * старый API поля не присылает.
   */
  isPinned?: boolean;
  /** В архиве Telegram. Зеркало архива на этом аккаунте. Читать защищённо. */
  tgArchived?: boolean;
  internalStatus: ChatInternalStatus;
  assignedManager: UserSummaryDto | null;
  assignedSenior: UserSummaryDto | null;
  tags: string[];
  /** Папки, в которых лежит чат. Пустой массив — не разложен. */
  folderIds: string[];
  /** Только для тех, кто может менять метаданные. Иначе поле отсутствует. */
  internalNotes?: string | null;
}

export interface TelegramMessageDto {
  id: string;
  chatId: string;
  direction: 'INBOUND' | 'OUTBOUND';
  deliveryState: 'PENDING' | 'SENT' | 'FAILED' | 'EDITED' | 'DELETED';
  /** Кто из наших сотрудников отправил. Для входящих — null. */
  sentBy: UserSummaryDto | null;
  /** Текст сообщения либо подпись к вложению. */
  text: string | null;
  hasMedia: boolean;
  /** Разметка текста: жирный, курсив, ссылки, премиальные эмодзи. */
  entities: TelegramTextEntityDto[];
  /**
   * Описание вложения. Присутствует только у сообщений из TDLib: у наших
   * записей в БД медиа-метаданных нет, и подставлять их неоткуда.
   */
  media: TelegramMediaDto | null;
  sentAt: string | null;
  editedAt: string | null;
  /**
   * Реакции на сообщение.
   *
   * Может отсутствовать: у наших записей в БД реакций нет, они приходят
   * только из TDLib. Читать это поле нужно защищённо — клиент и сервер
   * обновляются не одновременно.
   */
  reactions?: TelegramReactionDto[];
  /**
   * Служебное сообщение: «закрепил», «сменил фото чата» и подобное.
   *
   * Показывается строкой по центру, а не пузырём: отметка системы никому не
   * адресована и отвечать на неё не на что. null у обычных сообщений.
   */
  service?: string | null;
  /**
   * Звонок — пузырь с трубкой и длительностью, а не вложение. Читать
   * защищённо: у старого сервера поля нет.
   */
  call?: TelegramCallDto | null;
  /**
   * Прочитано ли собеседником — только для исходящих. Одна галочка —
   * отправлено, две — прочитано, как в Telegram. Отсутствие поля (старый
   * сервер) — «неизвестно»: показывается одна галочка.
   */
  read?: boolean;
  /**
   * Идентификатор сообщения в Telegram. Нужен, чтобы по событию «прочитано
   * до N» перевести галочки без перезагрузки истории. У наших записей `id` —
   * UUID, и без этого поля их не с чем сравнить.
   */
  telegramMessageId?: string | null;
}

export interface TelegramCallDto {
  video: boolean;
  /** Секунды разговора; 0 — соединения не было. */
  duration: number;
  /** Исход относительно нашего аккаунта. */
  outcome: 'completed' | 'missed' | 'cancelled' | 'declined' | 'disconnected';
}

export interface TelegramReactionDto {
  emoji: string;
  count: number;
  /** Поставлена ли реакция с нашего аккаунта. */
  chosen: boolean;
}

export interface TelegramTextEntityDto {
  type: string;
  offset: number;
  length: number;
  customEmojiId?: string;
  url?: string;
}

export interface TelegramMediaDto {
  kind:
    | 'photo'
    | 'video'
    | 'animation'
    | 'document'
    | 'voice'
    | 'audio'
    | 'videoNote'
    | 'sticker'
    | 'other';
  fileName: string | null;
  mimeType: string | null;
  size: number | null;
  duration: number | null;
  width: number | null;
  height: number | null;
  emoji: string | null;
  /** Описание словами для kind = other: «Опрос: …», «Контакт: …». */
  label?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Аудит
// ─────────────────────────────────────────────────────────────────────────────

export interface AuditEventDto {
  id: string;
  occurredAt: string;
  action: string;
  outcome: 'SUCCESS' | 'DENIED' | 'ERROR';
  actor: UserSummaryDto | null;
  /**
   * Автор, чья карточка удалена.
   *
   * Заполнен вместо `actor`, когда сотрудника удалили: имя и роль сохраняются
   * внутри самой записи, чтобы журнал не приписывал его действия «системе».
   */
  deletedActor?: { displayName: string; role: string } | null;
  telegramAccountId: string | null;
  chatId: string | null;
  messageId: string | null;
  targetUser: UserSummaryDto | null;
  /** Предмет действия, чья карточка удалена. См. `deletedActor`. */
  deletedTarget?: { displayName: string; role: string } | null;
  metadata: Record<string, unknown> | null;
  ip: string | null;
  deviceLabel: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Общие обёртки
// ─────────────────────────────────────────────────────────────────────────────

export interface Paginated<T> {
  items: T[];
  /** Курсорная пагинация: offset на растущих таблицах даёт дубли и пропуски. */
  nextCursor: string | null;
  total?: number;
}

export interface ApiErrorDto {
  error: {
    code: string;
    message: string;
    /** Детали валидации. Для 403 всегда пусто — см. PERMISSIONS.md §1. */
    details?: unknown;
  };
}

/** Статус собеседника в шапке чата. */
export interface TelegramPresenceDto {
  status: string | null;
  online: boolean;
  blocked: boolean;
}
